import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";
import { GraduationCap, Award, Mail } from "lucide-react";

const PANEL_IMG =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_58b37413-1479-4a3f-927c-0029e3907f9e/artifacts/s22xg76n_Tasha_Panel3_BottomRight.jpeg";

const EXPERTISE = [
  "Home care & healthcare agency development",
  "Medicaid readiness & regulatory compliance",
  "Leadership & workforce strategy",
  "Caregiver training & staff development",
  "Policy, procedure & operational systems",
  "Recruitment, retention & organizational growth",
];

export const HostBio = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="host" data-testid="host-section" ref={ref} className="relative bg-ink py-20 md:py-32">
      <div className="container-tasha grid items-center gap-14 lg:grid-cols-2">
        {/* Clipped portrait frame */}
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="relative mx-auto w-full max-w-md"
        >
          <div className="relative overflow-hidden rounded-[2rem] border border-gold/20">
            <motion.img
              src={PANEL_IMG}
              alt="Natasha Mays, host of the #AskTasha Home Health Care Agency Class Series"
              style={{ y }}
              className="h-full w-full scale-110 object-cover"
              data-testid="host-image"
            />
            <div className="pointer-events-none absolute inset-0 rounded-[2rem] ring-1 ring-inset ring-gold/20" />
          </div>
          <div className="absolute -bottom-5 left-2 rounded-2xl border border-gold/30 bg-ink-elevated px-5 py-3 backdrop-blur-sm sm:-bottom-6 sm:px-6 sm:py-4 md:-left-8">
            <span className="gold-text-gradient font-serif text-4xl font-semibold">20+</span>
            <p className="font-sans text-xs uppercase tracking-widest text-cream/60">Years Experience</p>
          </div>
        </motion.div>

        {/* Bio */}
        <motion.div variants={stagger} initial="hidden" whileInView="visible" viewport={viewportOnce}>
          <motion.span variants={fadeUp} className="overline">Your Host</motion.span>
          <motion.h2 variants={fadeUp} className="mt-4 font-serif text-5xl font-semibold tracking-tight text-cream md:text-6xl">
            Natasha Mays
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-2 font-script text-3xl text-gold">
            Strategy. Structure. Launch Clarity.
          </motion.p>
          <motion.p variants={fadeUp} className="mt-6 font-sans text-base font-light leading-relaxed text-cream/75">
            More than two decades of executive leadership in human resources,
            organizational development, regulatory compliance, and business
            operations — leading multi-site teams for GE, Scientific Games,
            InterContinental Hotels Group, Delta Apparel, and Schindler Elevator.
            As a healthcare entrepreneur and trainer, she builds home care
            organizations, caregiver training programs, compliance systems, and
            operational playbooks that actually work.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-8 grid gap-2 sm:grid-cols-2">
            {EXPERTISE.map((e, i) => (
              <div key={i} className="flex items-start gap-2 font-sans text-sm text-cream/70">
                <span className="mt-1.5 h-1.5 w-1.5 flex-none rounded-full bg-gold" />
                {e}
              </div>
            ))}
          </motion.div>

          <motion.div variants={fadeUp} className="mt-8 flex flex-wrap gap-6 border-t border-cream/10 pt-6">
            <div className="flex items-center gap-3">
              <GraduationCap className="text-gold" size={22} />
              <div>
                <p className="font-sans text-sm text-cream/85">MBA Marketing · BA Psychology</p>
                <p className="font-sans text-xs text-cream/50">Clark Atlanta University</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Award className="text-gold" size={22} />
              <div>
                <p className="font-sans text-sm text-cream/85">Green Belt Certification</p>
                <p className="font-sans text-xs text-cream/50">University of Michigan</p>
              </div>
            </div>
          </motion.div>

          <motion.a
            variants={fadeUp}
            href="mailto:contact@gotoasktasha.com"
            data-testid="host-email-link"
            className="mt-8 inline-flex items-center gap-2 font-sans text-sm text-gold transition-colors hover:text-gold-light"
          >
            <Mail size={16} /> contact@gotoasktasha.com
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default HostBio;
